import React, {useEffect, useRef, useState, useMemo} from 'react';
import Main from "../../modules/main/main";
import { apiForms } from "../../services/api/forms";
import {useNavigate, useParams} from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Paper from "@mui/material/Paper";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import parse from 'html-react-parser';

function FormBuilder(props) {
    const isInitialMount = useRef(true);
    const { id } = useParams();
    let navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState('');
    const [createMode, setCreateMode] = useState(true);
    const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
    const [editingFieldIndex, setEditingFieldIndex] = useState(null);
    const [editingStepIndex, setEditingStepIndex] = useState(null);
    const [currentField, setCurrentField] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [previewActiveStep, setPreviewActiveStep] = useState(0);

    const {formState: { errors }, handleSubmit, reset, watch, control, setValue, getValues} = useForm({
        defaultValues: {
            name: '',
            slug: '',
            description: '',
            is_active: true,
            steps: []
        }
    });

    let breadcrumbs = [
        {"name": "Dashboard", "active": false},
        {"name": "Forms", "link": "/forms", "active": false},
        {"name": createMode ? "Create Form" : "Edit Form", "active": true},
    ];

    const fetchData = React.useCallback(async(id) => {
        try {
            const res = await apiForms.getSingle(id);
            
            // Check if form exists in response
            if (!res || !res.form) {
                if (res && res.error) {
                    setApiError(res.error);
                } else {
                    setApiError('Form not found');
                }
                navigate('/forms');
                setLoading(false);
                return;
            }
            
            const form = res.form;
            reset({
                name: form.name || '',
                slug: form.slug || '',
                description: form.description || '',
                is_active: form.is_active !== undefined ? form.is_active : true,
                steps: form.steps || []
            });
            setLoading(false);
        } catch (err) {
            console.error('Error loading form:', err);
            let errorMessage = 'Failed to load form';
            if (err && err.response && err.response.data) {
                errorMessage = err.response.data.error || err.response.data.message || errorMessage;
            } else if (err && err.message) {
                errorMessage = err.message;
            }
            setApiError(errorMessage);
            setLoading(false);
        }
    }, [navigate, reset])

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            if(id){
                setCreateMode(false);
                fetchData(id);
            } else {
                setLoading(false);
            }
        }
    }, [id, fetchData])

    const onSave = async (data) => {
        setLoading(true);
        setApiError('');
        
        try {
            let result;
            if(createMode){
                result = await apiForms.post(data);
                if(result.form && result.form.id){
                    navigate(`/forms/${result.form.id}`);
                }
            } else {
                result = await apiForms.patch(id, data);
            }
            
            if(result && result.status){
                setApiError(result.data || 'An error occurred');
            } else {
                // Success - could show a success message
                if(createMode) {
                    // Already navigated above
                }
            }
        } catch (err) {
            setApiError('Failed to save form');
        } finally {
            setLoading(false);
        }
    }

    const addStep = () => {
        const currentSteps = getValues('steps') || [];
        const newStep = {
            label: `Step ${currentSteps.length + 1}`,
            description: '',
            optional: false,
            fields: []
        };
        setValue('steps', [...currentSteps, newStep]);
    }

    const deleteStep = (stepIndex) => {
        const currentSteps = getValues('steps') || [];
        const newSteps = currentSteps.filter((_, index) => index !== stepIndex);
        setValue('steps', newSteps);
    }

    const openFieldDialog = (stepIndex, fieldIndex = null) => {
        setEditingStepIndex(stepIndex);
        setEditingFieldIndex(fieldIndex);
        if (fieldIndex !== null) {
            const steps = getValues('steps') || [];
            setCurrentField({...steps[stepIndex].fields[fieldIndex]});
        } else {
            setCurrentField({
                name: '',
                type: 'text',
                label: '',
                required: false,
                error_text: '',
                width: 6
            });
        }
        setFieldDialogOpen(true);
    }

    const closeFieldDialog = () => {
        setFieldDialogOpen(false);
        setEditingStepIndex(null);
        setEditingFieldIndex(null);
        setCurrentField(null);
    }

    const saveField = () => {
        if (editingStepIndex === null || !currentField) return;
        
        const steps = getValues('steps') || [];
        const step = {...steps[editingStepIndex]};
        
        if (editingFieldIndex !== null) {
            // Edit existing field
            step.fields[editingFieldIndex] = {...currentField};
        } else {
            // Add new field
            step.fields = [...(step.fields || []), {...currentField}];
        }
        
        steps[editingStepIndex] = step;
        setValue('steps', steps);
        closeFieldDialog();
    }

    const deleteField = (stepIndex, fieldIndex) => {
        const steps = getValues('steps') || [];
        const step = {...steps[stepIndex]};
        step.fields = step.fields.filter((_, index) => index !== fieldIndex);
        steps[stepIndex] = step;
        setValue('steps', steps);
    }

    const updateStep = (stepIndex, field, value) => {
        const steps = getValues('steps') || [];
        const step = {...steps[stepIndex]};
        step[field] = value;
        steps[stepIndex] = step;
        setValue('steps', steps);
    }

    const fieldTypes = [
        { value: 'text', label: 'Text' },
        { value: 'number', label: 'Number' },
        { value: 'date', label: 'Date' },
        { value: 'select', label: 'Select/Dropdown' },
        { value: 'checkbox', label: 'Checkbox' },
        { value: 'file', label: 'File Upload' },
        { value: 'section', label: 'Section Divider' },
    ];

    const watchedSteps = watch('steps');
    
    // Steps for the form builder (always available)
    const steps = useMemo(() => watchedSteps || [], [watchedSteps]);
    
    // Deep clone steps for preview to ensure it doesn't mutate the original
    const memoizedSteps = useMemo(() => {
        if (!showPreview || !steps || steps.length === 0) return [];
        try {
            return JSON.parse(JSON.stringify(steps));
        } catch (e) {
            return steps;
        }
    }, [showPreview, steps]);



    return (
        <>
        <Main title={createMode ? "Create Form" : "Edit Form"} breadcrumbs={breadcrumbs} loading={loading}>
            {apiError && <div className="alert alert-danger">
                <h5><i className="icon fas fa-ban"></i> Error!</h5>
                {apiError}
            </div>}
            
            <form onSubmit={handleSubmit(onSave)}>
                <Box sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Controller
                                name="name"
                                control={control}
                                rules={{ required: 'Form name is required' }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Form Name"
                                        error={!!errors.name}
                                        helperText={errors.name?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="slug"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Slug (auto-generated if empty)"
                                        helperText="URL-friendly identifier"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="description"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Description"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="is_active"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={field.value}
                                                onChange={field.onChange}
                                            />
                                        }
                                        label="Active"
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Form Steps</Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                type="button"
                                variant="outlined"
                                startIcon={showPreview ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                onClick={() => {
                                    const newValue = !showPreview;
                                    setShowPreview(newValue);
                                    if (newValue) {
                                        setPreviewActiveStep(0);
                                    }
                                }}
                            >
                                {showPreview ? 'Hide Preview' : 'Show Preview'}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={addStep}
                            >
                                Add Step
                            </Button>
                        </Box>
                    </Box>

                    {steps.map((step, stepIndex) => (
                        <Accordion key={stepIndex} defaultExpanded={stepIndex === 0}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 2 }}>
                                    <Typography>{step.label || `Step ${stepIndex + 1}`}</Typography>
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteStep(stepIndex);
                                        }}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Step Label"
                                            value={step.label || ''}
                                            onChange={(e) => updateStep(stepIndex, 'label', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Step Description (HTML supported)"
                                            value={step.description || ''}
                                            onChange={(e) => updateStep(stepIndex, 'description', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={step.optional || false}
                                                    onChange={(e) => updateStep(stepIndex, 'optional', e.target.checked)}
                                                />
                                            }
                                            label="Optional Step"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 2 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="subtitle1">Fields</Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => openFieldDialog(stepIndex)}
                                            >
                                                Add Field
                                            </Button>
                                        </Box>
                                        {step.fields && step.fields.map((field, fieldIndex) => (
                                            <Box key={fieldIndex} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="subtitle2">
                                                        {field.label || field.name || `Field ${fieldIndex + 1}`} ({field.type})
                                                    </Typography>
                                                    <Box>
                                                        <Button
                                                            size="small"
                                                            onClick={() => openFieldDialog(stepIndex, fieldIndex)}
                                                            sx={{ mr: 1 }}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            onClick={() => deleteField(stepIndex, fieldIndex)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </Box>
                                                </Box>
                                                {field.section && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Section: {field.section}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, p: 2 }}>
                    <Button variant="outlined" onClick={() => navigate('/forms')}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        Save Form
                    </Button>
                </Box>
            </form>
        </Main>

        {/* Form Preview Dialog - Always render, control with open prop */}
            <Dialog 
            open={showPreview}
            onClose={() => {
                setShowPreview(false);
            }}
            maxWidth="lg" 
            fullWidth
            PaperProps={{
                sx: { 
                    height: '90vh', 
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Form Preview</Typography>
                    <IconButton onClick={() => {
                        setShowPreview(false);
                    }}>
                        <VisibilityOffIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, overflow: 'auto' }}>
                <Box sx={{ p: 3 }}>
                    {showPreview && memoizedSteps && memoizedSteps.length > 0 ? (
                        <Box>
                            <Stepper activeStep={previewActiveStep} alternativeLabel sx={{ mb: 4 }}>
                                {memoizedSteps.map((step, index) => (
                                    <Step key={index}>
                                        <StepLabel>{step.label || `Step ${index + 1}`}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            <Divider sx={{ mb: 3 }} />
                            <Box>
                                {memoizedSteps[previewActiveStep]?.description && (
                                    <Box sx={{ mb: 3 }}>
                                        {parse(memoizedSteps[previewActiveStep].description)}
                                    </Box>
                                )}
                                <Grid container spacing={2}>
                                    {memoizedSteps[previewActiveStep]?.fields?.map((field, fieldIndex) => {
                                        if (field.type === 'section') {
                                            return (
                                                <Grid item xs={12} key={fieldIndex}>
                                                    <Divider textAlign="left" sx={{ fontWeight: "bold", my: 2 }}>
                                                        {field.section || field.label}
                                                    </Divider>
                                                </Grid>
                                            );
                                        }
                                        const width = field.width || 6;
                                        return (
                                            <Grid item xs={12} sm={width} key={fieldIndex}>
                                                <TextField
                                                    fullWidth
                                                    label={field.label || field.name}
                                                    type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                                    required={field.required}
                                                    disabled
                                                    placeholder={`${field.type} field${field.required ? ' (required)' : ''}`}
                                                    multiline={field.type === 'text' && field.rows > 1}
                                                    rows={field.rows || 1}
                                                    helperText={field.required ? field.error_text : ''}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        if (previewActiveStep > 0) {
                                            setPreviewActiveStep(previewActiveStep - 1);
                                        }
                                    }}
                                    disabled={previewActiveStep === 0}
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        if (previewActiveStep < memoizedSteps.length - 1) {
                                            setPreviewActiveStep(previewActiveStep + 1);
                                        } else {
                                            alert('This is a preview. Form submission is disabled.');
                                        }
                                    }}
                                >
                                    {previewActiveStep === memoizedSteps.length - 1 ? 'Complete' : 'Next'}
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                Add at least one step to see the preview
                            </Typography>
                        </Paper>
                    )}
                </Box>
            </DialogContent>
        </Dialog>

            {/* Field Dialog */}
            <Dialog open={fieldDialogOpen} onClose={closeFieldDialog} maxWidth="md" fullWidth>
                <DialogTitle>{(editingFieldIndex !== null ? 'Edit' : 'Add')} Field</DialogTitle>
                <DialogContent>
                    {currentField && (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Field Name (internal identifier)"
                                    value={currentField.name || ''}
                                    onChange={(e) => setCurrentField({...currentField, name: e.target.value})}
                                    helperText="Used as the field identifier in the database"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Field Type</InputLabel>
                                    <Select
                                        value={currentField.type || 'text'}
                                        label="Field Type"
                                        onChange={(e) => setCurrentField({...currentField, type: e.target.value})}
                                    >
                                        {fieldTypes.map(type => (
                                            <MenuItem key={type.value} value={type.value}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Field Label"
                                    value={currentField.label || ''}
                                    onChange={(e) => setCurrentField({...currentField, label: e.target.value})}
                                />
                            </Grid>
                            {currentField.type !== 'section' && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={currentField.required || false}
                                                    onChange={(e) => setCurrentField({...currentField, required: e.target.checked})}
                                                />
                                            }
                                            label="Required"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Width (1-12 grid columns)"
                                            value={currentField.width || 6}
                                            onChange={(e) => setCurrentField({...currentField, width: parseInt(e.target.value) || 6})}
                                            inputProps={{ min: 1, max: 12 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Error Message"
                                            value={currentField.error_text || ''}
                                            onChange={(e) => setCurrentField({...currentField, error_text: e.target.value})}
                                            helperText="Message shown when validation fails"
                                        />
                                    </Grid>
                                    {currentField.type === 'text' && (
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Rows (for multiline)"
                                                value={currentField.rows || 1}
                                                onChange={(e) => setCurrentField({...currentField, rows: parseInt(e.target.value) || 1})}
                                                inputProps={{ min: 1 }}
                                            />
                                        </Grid>
                                    )}
                                    {currentField.type === 'select' && (
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                label="Options (JSON format: {'key': 'value', 'key2': 'value2'})"
                                                value={currentField.options ? JSON.stringify(currentField.options, null, 2) : ''}
                                                onChange={(e) => {
                                                    try {
                                                        const options = e.target.value ? JSON.parse(e.target.value) : {};
                                                        setCurrentField({...currentField, options});
                                                    } catch (err) {
                                                        // Invalid JSON, keep as is
                                                    }
                                                }}
                                                helperText="Enter options as JSON object"
                                            />
                                        </Grid>
                                    )}
                                    {currentField.type === 'file' && (
                                        <>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="File Message (HTML)"
                                                    multiline
                                                    rows={3}
                                                    value={currentField.file_message || ''}
                                                    onChange={(e) => setCurrentField({...currentField, file_message: e.target.value})}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Sample URL"
                                                    value={currentField.sample_url || ''}
                                                    onChange={(e) => setCurrentField({...currentField, sample_url: e.target.value})}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Sample Message"
                                                    value={currentField.sample_message || ''}
                                                    onChange={(e) => setCurrentField({...currentField, sample_message: e.target.value})}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                </>
                            )}
                            {currentField.type === 'section' && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Section Title"
                                        value={currentField.section || ''}
                                        onChange={(e) => setCurrentField({...currentField, section: e.target.value})}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeFieldDialog}>Cancel</Button>
                    <Button onClick={saveField} variant="contained">
                        {editingFieldIndex !== null ? 'Update' : 'Add'} Field
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default FormBuilder;
